import { useEffect, useState } from 'react'
import { api, type AmpelTag, type FehlendesArchiv, type Pruefung } from '../api'
import { datumMittel, euro, menge } from '../format'
import { Ampel } from '../komponenten/Ampel'
import { Karte } from '../komponenten/Karte'

/* Wie die fünf Quellen heißen, wenn ein Mensch sie liest. Dieselbe
   Liste steht in `gastro.load.tagesquelle` — ein Test hält beide
   gegeneinander, denn zwei Wortlaute für eine Sache sind in diesem
   Projekt schon einmal 38 Stellen geworden (S-47). */
const QUELLE: Record<string, string> = {
  ARCHIVE: 'Kassenarchiv',
  RECEIPTS: 'Quittungsreport',
  VOIDS: 'Stornoreport',
  JOURNAL: 'Transaktionsbericht',
  TENDERS: 'Zahlungsbericht',
}

const benannt = (q: string) => QUELLE[q] ?? q

/**
 * Die unauffaelligste Ansicht - und die, ohne die alle anderen wertlos
 * sind. Sie beantwortet, ob man den Zahlen darueber trauen darf.
 */
export function Datenqualitaet({ von, bis, betriebe }: { von: string; bis: string; betriebe: string[] }) {
  const [tage, setTage] = useState<AmpelTag[]>([])
  const [gewaehlt, setGewaehlt] = useState<AmpelTag | null>(null)
  const [pruefungen, setPruefungen] = useState<Pruefung[] | null>(null)
  const [ohneArchive, setOhneArchive] = useState<FehlendesArchiv[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    Promise.all([
      api.datenqualitaet(von, bis, betriebe),
      api.offeneArchive(),
    ])
      .then(([x, archive]) => {
        if (!weg) {
          setTage(x.tage)
          setGewaehlt(x.tage[0] ?? null)
          setOhneArchive(archive.tage)
        }
      })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  useEffect(() => {
    if (!gewaehlt) { setPruefungen(null); return }
    let weg = false
    api.pruefungen(gewaehlt.tag, gewaehlt.betrieb)
      .then((x) => { if (!weg) setPruefungen(x) })
      .catch(() => { if (!weg) setPruefungen(null) })
    return () => { weg = true }
  }, [gewaehlt?.tag, gewaehlt?.betrieb])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !tage.length) return <div className="laedt">Wird geladen …</div>

  const offen = pruefungen?.filter((p) => !p.bestanden) ?? []

  return (
    <>
      {ohneArchive.length > 0 && (
        <div className="karte hervor" style={{ marginBottom: 16 }}>
          <h3>
            {ohneArchive.length === 1
              ? 'Ein Berichtstag wartet auf sein Kassenarchiv'
              : `${ohneArchive.length} Berichtstage warten auf ihr Kassenarchiv`}
          </h3>
          <p>
            Die Berichte sind bereits eingelesen, das Kassenarchiv fehlt
            noch. Gästezahl und Tischbelegungen stehen, Umsatz und
            Rechnungszahl nicht. Sobald das Archiv nachkommt, verschwindet
            der Eintrag von selbst.
          </p>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>Standort</th><th>Geschäftstag</th><th>Fehlt</th></tr></thead>
              <tbody>
                {ohneArchive.map((o) => (
                  <tr key={`${o.betrieb}-${o.tag}`}>
                    <td>{o.standort}</td>
                    <td>{datumMittel(o.tag)}</td>
                    <td><span className="marke warn">Kassenarchiv</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="gitter g-2-1">
      <Karte titel="Geschäftstage" neben={`${tage.length} Einträge`} flach>
        {/* Was „fehlt" heißt und was es nicht heißt. Ohne diesen Satz
            liest sich ein fehlender Stornoreport wie ein verlorener —
            und ein Kunde, der einmal umsonst gesucht hat, sieht beim
            zweiten Mal nicht mehr nach (S-45). */}
        <p className="merker" style={{ margin: '0 0 12px' }}>
          <b>fehlt</b> heißt: Für diesen Tag liegt keine Zeile dieser
          Quelle vor. Beim Stornoreport kann das auch bedeuten, dass
          nichts storniert wurde — ein leerer Bericht trägt keinen
          Geschäftstag und ist von einem nicht gelieferten nicht zu
          unterscheiden. Die anderen vier Quellen sind an einem Tag mit
          Umsatz immer gefüllt.
        </p>
        <div style={{ maxHeight: 560, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Tag</th><th>Standort</th><th>Zustand</th>
                <th>Quellen</th><th>fehlt</th><th>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {tage.map((t) => (
                <tr key={t.tag + t.betrieb}
                    onClick={() => setGewaehlt(t)}
                    style={{
                      cursor: 'pointer',
                      background: gewaehlt?.tag === t.tag && gewaehlt?.betrieb === t.betrieb
                        ? 'var(--akzent-weich)' : undefined,
                    }}>
                  <td>{datumMittel(t.tag)}</td>
                  <td>{t.betrieb_name}</td>
                  <td><Ampel zustand={t.ampel} /></td>
                  <td className="dezent">
                    {t.quellen.map(benannt).join(', ') || '–'}
                  </td>
                  {/* **Die Spalte, die vorher nichts sagen konnte.**
                      `sources_missing` wurde von keinem Weg
                      beschrieben; jetzt wird sie beim Lesen gebildet.
                      Fünf Dateien je Standort und Tag — bei zwölf
                      Standorten sechzig, und von Hand sieht das
                      niemand nach. */}
                  <td>
                    {t.fehlend.length === 0
                      ? <span className="dezent">–</span>
                      : t.fehlend.map((q) => (
                          <span key={q} className="marke warn">{benannt(q)}</span>
                        ))}
                  </td>
                  {/* Heute gibt es genau eine Ausfallzahlart. Kommt
                      eine zweite hinzu, muss der Name aus den Daten
                      kommen; "Kassenstreik" wäre dann nicht mehr für
                      alle Vorfälle richtig. */}
                  <td>
                    {t.ausfall_anzahl === 0
                      ? <span className="dezent">–</span>
                      : (
                        <>
                          <span className="marke warn">Kassenstreik</span>
                          <span className="dezent" style={{ marginLeft: 6 }}>
                            {t.ausfall_anzahl} {t.ausfall_anzahl === 1 ? 'Vorfall' : 'Vorfälle'}
                            {' · '}
                            {euro(t.ausfall_betrag)}
                          </span>
                        </>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Karte>

      <Karte titel="Abgleich im Einzelnen"
             neben={gewaehlt ? `${datumMittel(gewaehlt.tag)} · ${gewaehlt.betrieb_name}` : undefined}>
        {!pruefungen ? (
          <div className="leer">Einen Tag in der Liste wählen.</div>
        ) : !pruefungen.length ? (
          <div className="leer">Für diesen Tag liegt kein Abgleich vor.</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--schrift-2)' }}>
              {pruefungen.length - offen.length} von {pruefungen.length} Prüfungen bestanden
            </div>
            <table>
              <thead>
                <tr>
                  <th>Prüfung</th>
                  <th className="r">Abschluss</th>
                  <th className="r">berechnet</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pruefungen.map((p) => (
                  <tr key={p.code}>
                    <td>
                      {p.label}
                      {p.hinweis && (
                        <div className="dezent" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.45 }}>
                          {p.hinweis}
                        </div>
                      )}
                    </td>
                    <td className="r zahl">
                      {p.soll_text ?? (p.einheit === 'ANZAHL' ? menge(p.soll) : euro(p.soll))}
                    </td>
                    <td className="r zahl">
                      {p.ist_text ?? (p.einheit === 'ANZAHL' ? menge(p.ist) : euro(p.ist))}
                    </td>
                    <td className="r" style={{ whiteSpace: 'nowrap' }}>
                      {p.bestanden ? (
                        <span className="ampel gruen">
                          <span className="punkt" aria-hidden="true" />OK
                        </span>
                      ) : (
                        <span className={`ampel ${p.severity === 'SUM' ? 'rot' : 'gelb'}`}>
                          <span className="punkt" aria-hidden="true" />
                          {p.severity === 'SUM' ? 'Abweichung' : 'Prüfen'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="dezent" style={{ fontSize: 11, marginTop: 14, marginBottom: 0, lineHeight: 1.55 }}>
              Eine Summenprüfung, die abweicht, macht den Tag rot: es fehlt Geld
              oder eine Position ist falsch eingeordnet. Eine Prüfung auf
              Metadaten des Exports macht ihn gelb — die Kasse ist sich über sich
              selbst uneinig, die Zahlen bleiben belastbar.
            </p>
          </>
        )}
      </Karte>
      </div>
    </>
  )
}
